import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import axios from 'axios';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Users, UserPlus, Check, X, MessageSquare, Search, Send, MapPin, Filter, ChevronDown, ChevronUp, Eye } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { resolveImageUrl } from '../utils/imageUrl';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AGE_RANGES = ['18-30', '31-45', '46-65', '65+'];

// Format a message timestamp: "Today 14:32" / "Yesterday 09:15" / "Mon 22/05 14:32"
const formatMessageTime = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    const now = new Date();
    const same = (a, b) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
    const y = new Date(now); y.setDate(now.getDate() - 1);
    const time = d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    if (same(d, now)) return `Today ${time}`;
    if (same(d, y)) return `Yesterday ${time}`;
    return `${d.toLocaleDateString([], { day: '2-digit', month: '2-digit', year: 'numeric' })} ${time}`;
};

export default function ConnectionsPage() {
    const { user } = useAuth();
    const [searchParams, setSearchParams] = useSearchParams();
    const [connections, setConnections] = useState([]);
    const [members, setMembers] = useState([]);
    const [loading, setLoading] = useState(true);
    
    // Filters
    const [searchQuery, setSearchQuery] = useState('');
    const [filterCountry, setFilterCountry] = useState('');
    const [filterCity, setFilterCity] = useState('');
    const [filterAgeRange, setFilterAgeRange] = useState('');
    const [showFilters, setShowFilters] = useState(false);
    
    // Connection request
    const [connectDialogOpen, setConnectDialogOpen] = useState(false);
    const [selectedMember, setSelectedMember] = useState(null);
    const [connectionMessage, setConnectionMessage] = useState('');
    const [sending, setSending] = useState(false);
    
    // Messaging
    const [chatOpen, setChatOpen] = useState(false);
    const [activeConnection, setActiveConnection] = useState(null);
    const [messages, setMessages] = useState([]);
    const [newMessage, setNewMessage] = useState('');
    const [loadingMessages, setLoadingMessages] = useState(false);
    const [sendingMessage, setSendingMessage] = useState(false);

    // Profile preview
    const [profileOpen, setProfileOpen] = useState(false);
    const [profileMember, setProfileMember] = useState(null);
    const [profileLoading, setProfileLoading] = useState(false);

    const openProfile = async (memberLite) => {
        setProfileMember(memberLite); // show what we have immediately
        setProfileOpen(true);
        setProfileLoading(true);
        try {
            const { data } = await axios.get(`${API_URL}/api/connections/members/${memberLite.id}`, { withCredentials: true });
            setProfileMember(data);
        } catch (err) {
            // ignore — keep lite version
        } finally {
            setProfileLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, [filterCountry, filterCity, filterAgeRange]);

    // Live updates: refresh connection badges every 15s, and the open chat every 8s
    useEffect(() => {
        const refresh = () => axios.get(`${API_URL}/api/connections`, { withCredentials: true })
            .then(r => setConnections(r.data)).catch(() => {});
        const t = setInterval(refresh, 15000);
        window.addEventListener('m4l:notifications-refresh', refresh);
        return () => { clearInterval(t); window.removeEventListener('m4l:notifications-refresh', refresh); };
    }, []);

    useEffect(() => {
        if (!chatOpen || !activeConnection) return;
        const t = setInterval(async () => {
            try {
                const { data } = await axios.get(`${API_URL}/api/connections/${activeConnection.id}/messages`, { withCredentials: true });
                setMessages(prev => (data.length !== prev.length ? data : prev));
            } catch (err) { /* ignore */ }
        }, 8000);
        return () => clearInterval(t);
    }, [chatOpen, activeConnection]);

    // Auto-open a chat when navigated via a notification: /connections?open=<connectionId>
    useEffect(() => {
        const openId = searchParams.get('open');
        if (!openId || connections.length === 0) return;
        const conn = connections.find(c => c.id === openId);
        if (conn) {
            openChat(conn);
            // clean up URL so refresh doesn't re-open
            const next = new URLSearchParams(searchParams);
            next.delete('open');
            setSearchParams(next, { replace: true });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [connections, searchParams]);

    const fetchData = async () => {
        try {
            const [connectionsRes, membersRes] = await Promise.all([
                axios.get(`${API_URL}/api/connections`, { withCredentials: true }),
                axios.get(`${API_URL}/api/connections/members`, { 
                    withCredentials: true,
                    params: {
                        country: filterCountry || undefined,
                        city: filterCity || undefined,
                        age_range: filterAgeRange || undefined
                    }
                })
            ]);
            setConnections(connectionsRes.data);
            setMembers(membersRes.data);
        } catch (error) {
            console.error('Error fetching data:', error);
            toast.error('Failed to load connections');
        } finally {
            setLoading(false);
        }
    };

    const handleSendRequest = async () => {
        if (!selectedMember) return;
        
        setSending(true);
        try {
            await axios.post(
                `${API_URL}/api/connections/request`,
                { to_user_id: selectedMember.id, message: connectionMessage },
                { withCredentials: true }
            );
            toast.success('Connection request sent!');
            setConnectDialogOpen(false);
            setSelectedMember(null);
            setConnectionMessage('');
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to send request');
        } finally {
            setSending(false);
        }
    };

    const handleAccept = async (connectionId) => {
        try {
            await axios.post(`${API_URL}/api/connections/${connectionId}/accept`, {}, { withCredentials: true });
            toast.success('Connection accepted!');
            fetchData();
        } catch (error) {
            toast.error('Failed to accept connection');
        }
    };

    const handleReject = async (connectionId) => {
        try {
            await axios.post(`${API_URL}/api/connections/${connectionId}/reject`, {}, { withCredentials: true });
            toast.info('Connection rejected');
            fetchData();
        } catch (error) {
            toast.error('Failed to reject connection');
        }
    };

    const openChat = async (connection) => {
        setActiveConnection(connection);
        setChatOpen(true);
        setLoadingMessages(true);
        
        try {
            const { data } = await axios.get(
                `${API_URL}/api/connections/${connection.id}/messages`,
                { withCredentials: true }
            );
            setMessages(data);
            if (connection.unread_count > 0 || connection.accepted_unseen) {
                setConnections(prev => prev.map(c => c.id === connection.id ? { ...c, unread_count: 0, accepted_unseen: false } : c));
            }
            window.dispatchEvent(new Event('m4l:notifications-refresh'));
        } catch (error) {
            console.error('Error loading messages:', error);
            toast.error('Failed to load messages');
        } finally {
            setLoadingMessages(false);
        }
    };

    const handleSendMessage = async (e) => {
        e.preventDefault();
        if (!newMessage.trim() || !activeConnection) return;
        
        setSendingMessage(true);
        try {
            const { data } = await axios.post(
                `${API_URL}/api/connections/${activeConnection.id}/messages`,
                { content: newMessage },
                { withCredentials: true }
            );
            setMessages([...messages, data]);
            setNewMessage('');
        } catch (error) {
            toast.error('Failed to send message');
        } finally {
            setSendingMessage(false);
        }
    };

    const pendingReceived = connections.filter(c => c.to_user_id === user?.id && c.status === 'pending');
    const pendingSent = connections.filter(c => c.from_user_id === user?.id && c.status === 'pending');
    const accepted = connections
        .filter(c => c.status === 'accepted')
        .sort((a, b) => (b.unread_count || 0) - (a.unread_count || 0));

    // Filter members not already connected
    const connectedUserIds = connections.map(c => 
        c.from_user_id === user?.id ? c.to_user_id : c.from_user_id
    );
    
    const availableMembers = members.filter(m => 
        !connectedUserIds.includes(m.id) &&
        m.name?.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const getTierBadgeClass = (tier) => {
        switch (tier) {
            case 'seeker': return 'tier-badge tier-seeker';
            case 'walker': return 'tier-badge tier-walker';
            case 'architect': return 'tier-badge tier-architect';
            case 'partnership': return 'tier-badge tier-partnership';
            default: return 'tier-badge bg-gray-100 text-gray-600';
        }
    };

    // Check connection limits
    const connectionLimit = user?.membership_tier === 'walker' ? 15 : (user?.membership_tier === 'architect' || user?.membership_tier === 'partnership') ? -1 : 0;
    const canSendMore = connectionLimit === -1 || (user?.connections_sent_this_month || 0) < connectionLimit;

    return (
        <div className="min-h-screen bg-[#F9F6F0]">
            <Navbar />

            <main className="pt-24 pb-16" data-testid="connections-page">
                <div className="max-w-6xl mx-auto px-6 sm:px-12">
                    {/* Header */}
                    <div className="mb-8">
                        <p className="overline mb-2">Community</p>
                        <h1 className="heading-2 mb-4">Connections</h1>
                        <p className="body-text">Connect with fellow seekers on your journey.</p>
                    </div>

                    {/* Connection Limit Info */}
                    {connectionLimit > 0 && (
                        <div className="mb-6 p-4 bg-[#F2EBE5] rounded-sm" data-testid="connection-limit-info">
                            <p className="font-sans text-sm text-[#4A4A68]">
                                Monthly connection requests: {user?.connections_sent_this_month || 0} / {connectionLimit} used
                                {!canSendMore && <span className="text-red-500 ml-2">(Limit reached)</span>}
                            </p>
                        </div>
                    )}

                    {/* Tabs/Sections */}
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                        
                        {/* Left: Member Discovery */}
                        <div className="lg:col-span-2 space-y-6">
                            <div className="bg-white border border-[#E5E0D8] rounded-sm p-6">
                                <div className="flex items-center justify-between mb-6">
                                    <h2 className="heading-3">Find Members</h2>
                                    <button
                                        onClick={() => setShowFilters(!showFilters)}
                                        className="flex items-center gap-2 text-[#5B2D8E] text-sm"
                                    >
                                        <Filter className="w-4 h-4" />
                                        Filters
                                        {showFilters ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                                    </button>
                                </div>

                                {/* Search */}
                                <div className="relative mb-4">
                                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#4A4A68]" />
                                    <input
                                        type="text"
                                        placeholder="Search by name..."
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                        className="w-full pl-12"
                                        data-testid="member-search"
                                    />
                                </div>

                                {/* Filters */}
                                {showFilters && (
                                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6 p-4 bg-[#F9F6F0] rounded-sm">
                                        <div>
                                            <label className="block text-xs text-[#4A4A68] mb-1">Country</label>
                                            <input
                                                type="text"
                                                placeholder="Any country"
                                                value={filterCountry}
                                                onChange={(e) => setFilterCountry(e.target.value)}
                                                className="w-full text-sm"
                                                data-testid="filter-country"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs text-[#4A4A68] mb-1">City</label>
                                            <input
                                                type="text"
                                                placeholder="Any city"
                                                value={filterCity}
                                                onChange={(e) => setFilterCity(e.target.value)}
                                                className="w-full text-sm"
                                                data-testid="filter-city"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs text-[#4A4A68] mb-1">Age Range</label>
                                            <select
                                                value={filterAgeRange}
                                                onChange={(e) => setFilterAgeRange(e.target.value)}
                                                className="w-full bg-white border border-[#E5E0D8] px-3 py-2 rounded-sm text-sm"
                                                data-testid="filter-age"
                                            >
                                                <option value="">Any age</option>
                                                {AGE_RANGES.map(range => (
                                                    <option key={range} value={range}>{range}</option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>
                                )}

                                {/* Member List */}
                                {loading ? (
                                    <div className="text-center py-8">
                                        <div className="w-8 h-8 border-3 border-[#5B2D8E] border-t-transparent rounded-full animate-spin mx-auto"></div>
                                    </div>
                                ) : availableMembers.length > 0 ? (
                                    <div className="space-y-3 max-h-96 overflow-y-auto" data-testid="member-list">
                                        {availableMembers.map(member => (
                                            <div
                                                key={member.id}
                                                className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-4 border border-[#E5E0D8] rounded-sm hover:border-[#5B2D8E] transition-colors"
                                                data-testid={`member-${member.id}`}
                                            >
                                                <div className="flex items-center gap-4 min-w-0">
                                                    {member.profile_image_url ? (
                                                        <img
                                                            src={resolveImageUrl(member.profile_image_url)}
                                                            alt={member.name}
                                                            className="w-12 h-12 rounded-full object-cover border border-[#E5E0D8]"
                                                        />
                                                    ) : (
                                                        <div className="w-12 h-12 bg-[#5B2D8E] rounded-full flex items-center justify-center text-white font-serif text-xl">
                                                            {member.name?.charAt(0) || '?'}
                                                        </div>
                                                    )}
                                                    <div className="min-w-0">
                                                        <button
                                                            onClick={() => openProfile(member)}
                                                            className="font-serif text-lg text-[#1A1A2E] hover:text-[#5B2D8E] transition-colors text-left"
                                                            data-testid={`member-name-${member.id}`}
                                                        >
                                                            {member.name}
                                                        </button>
                                                        <div className="flex items-center gap-2 text-xs text-[#4A4A68] flex-wrap">
                                                            <span className={getTierBadgeClass(member.membership_tier)}>
                                                                {member.membership_tier}
                                                            </span>
                                                            {member.city && member.country && (
                                                                <span className="flex items-center gap-1">
                                                                    <MapPin className="w-3 h-3" />
                                                                    {member.city}, {member.country}
                                                                </span>
                                                            )}
                                                            {member.age_range && (
                                                                <span>· {member.age_range}</span>
                                                            )}
                                                        </div>
                                                        {member.bio && (
                                                            <p className="text-sm text-[#4A4A68] mt-1 line-clamp-1">{member.bio}</p>
                                                        )}
                                                    </div>
                                                </div>
                                                {connectionLimit !== 0 && canSendMore && (
                                                    <div className="flex gap-2">
                                                        <button
                                                            onClick={() => openProfile(member)}
                                                            className="border border-[#5B2D8E] text-[#5B2D8E] bg-transparent hover:bg-[#5B2D8E] hover:text-white transition rounded-sm font-sans text-[11px] tracking-wider uppercase px-3 py-2 flex items-center justify-center gap-1.5"
                                                            data-testid={`view-profile-${member.id}`}
                                                        >
                                                            <Eye className="w-3.5 h-3.5" />
                                                            Profile
                                                        </button>
                                                        <button
                                                            onClick={() => {
                                                                setSelectedMember(member);
                                                                setConnectDialogOpen(true);
                                                            }}
                                                            className="bg-[#5B2D8E] text-white hover:bg-[#4A2475] transition rounded-sm font-sans text-[11px] tracking-wider uppercase px-3 py-2 flex items-center justify-center gap-1.5"
                                                            data-testid={`connect-${member.id}`}
                                                        >
                                                            <UserPlus className="w-3.5 h-3.5" />
                                                            Connect
                                                        </button>
                                                    </div>
                                                )}
                                                {(connectionLimit === 0 || !canSendMore) && (
                                                    <button
                                                        onClick={() => openProfile(member)}
                                                        className="btn-secondary text-xs px-4 py-2 flex items-center gap-1"
                                                        data-testid={`view-profile-${member.id}`}
                                                    >
                                                        <Eye className="w-4 h-4" />
                                                        Profile
                                                    </button>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="text-center py-8 text-[#4A4A68]">
                                        <Users className="w-8 h-8 mx-auto mb-2 text-[#D8B4E2]" />
                                        <p>No members found matching your criteria.</p>
                                    </div>
                                )}
                            </div>

                            {/* Pending Requests Received */}
                            {pendingReceived.length > 0 && (
                                <div className="bg-white border border-[#E5E0D8] rounded-sm p-6" data-testid="pending-requests">
                                    <h3 className="heading-3 mb-4">Pending Requests</h3>
                                    <div className="space-y-3">
                                        {pendingReceived.map(conn => (
                                            <div 
                                                key={conn.id}
                                                className="flex items-center justify-between p-4 border border-[#D8B4E2] bg-[#D8B4E2]/10 rounded-sm"
                                            >
                                                <div>
                                                    <p className="font-serif text-lg text-[#1A1A2E]">{conn.from_user_name}</p>
                                                    {conn.message && (
                                                        <p className="font-sans text-sm text-[#4A4A68] mt-1 italic">"{conn.message}"</p>
                                                    )}
                                                </div>
                                                <div className="flex gap-2">
                                                    <button
                                                        onClick={() => handleAccept(conn.id)}
                                                        className="p-2 bg-[#5B2D8E] text-white rounded-sm hover:bg-[#4A2475]"
                                                        data-testid={`accept-${conn.id}`}
                                                    >
                                                        <Check className="w-5 h-5" />
                                                    </button>
                                                    <button
                                                        onClick={() => handleReject(conn.id)}
                                                        className="p-2 bg-[#E5E0D8] text-[#4A4A68] rounded-sm hover:bg-[#D8B4E2]"
                                                        data-testid={`reject-${conn.id}`}
                                                    >
                                                        <X className="w-5 h-5" />
                                                    </button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Right: My Connections with Messaging */}
                        <div className="space-y-6">
                            <div className="bg-white border border-[#E5E0D8] rounded-sm p-6">
                                <h2 className="heading-3 mb-4">My Connections</h2>
                                
                                {accepted.length > 0 ? (
                                    <div className="space-y-3" data-testid="my-connections">
                                        {accepted.map(conn => {
                                            const otherName = conn.other_user_name || (conn.from_user_id === user?.id ? conn.to_user_name : conn.from_user_name);
                                            const otherId = conn.other_user_id || (conn.from_user_id === user?.id ? conn.to_user_id : conn.from_user_id);
                                            const unread = conn.unread_count || 0;
                                            const highlight = unread > 0 || conn.accepted_unseen;
                                            
                                            return (
                                                <div 
                                                    key={conn.id}
                                                    className={`p-4 border rounded-sm hover:border-[#5B2D8E] transition-colors ${highlight ? 'border-[#5B2D8E] bg-[#5B2D8E]/10' : 'border-[#E5E0D8]'}`}
                                                    data-testid={`connection-${conn.id}`}
                                                    data-unread={highlight ? 'true' : 'false'}
                                                >
                                                    <div className="flex items-center gap-3">
                                                        <div className="relative w-10 h-10 bg-[#5B2D8E] rounded-full flex items-center justify-center text-white font-serif shrink-0">
                                                            {otherName?.charAt(0) || '?'}
                                                            {unread > 0 && (
                                                                <span
                                                                    className="absolute -top-1.5 -right-1.5 min-w-[22px] h-[22px] px-1 rounded-full bg-[#D4AF37] text-[#1A1A2E] text-xs font-bold flex items-center justify-center ring-2 ring-white"
                                                                    data-testid={`connection-unread-${conn.id}`}
                                                                >
                                                                    {unread > 9 ? '9+' : unread}
                                                                </span>
                                                            )}
                                                        </div>
                                                        <div className="flex-grow min-w-0">
                                                            <p className={`font-serif text-[#1A1A2E] truncate ${highlight ? 'font-bold' : ''}`}>{otherName || 'Member'}</p>
                                                            <p className={`text-xs ${highlight ? 'text-[#5B2D8E] font-semibold' : 'text-[#4A4A68]'}`}>
                                                                {unread > 0
                                                                    ? `${unread} new message${unread > 1 ? 's' : ''}`
                                                                    : conn.accepted_unseen
                                                                        ? 'Accepted your request — say hello'
                                                                        : `Connected ${conn.accepted_at ? new Date(conn.accepted_at).toLocaleDateString() : ''}`}
                                                            </p>
                                                        </div>
                                                    </div>
                                                    <div className="flex gap-2 mt-3">
                                                        <button
                                                            onClick={() => openProfile({ id: otherId, name: otherName })}
                                                            className="flex-1 border border-[#5B2D8E] text-[#5B2D8E] bg-transparent hover:bg-[#5B2D8E] hover:text-white transition rounded-sm font-sans text-[11px] tracking-wider uppercase px-3 py-2 flex items-center justify-center gap-1.5"
                                                            data-testid={`view-conn-profile-${conn.id}`}
                                                        >
                                                            <Eye className="w-3.5 h-3.5" />
                                                            Profile
                                                        </button>
                                                        <button
                                                            onClick={() => openChat(conn)}
                                                            className="flex-1 bg-[#5B2D8E] text-white hover:bg-[#4A2475] transition rounded-sm font-sans text-[11px] tracking-wider uppercase px-3 py-2 flex items-center justify-center gap-1.5"
                                                            data-testid={`open-chat-${conn.id}`}
                                                        >
                                                            <MessageSquare className="w-3.5 h-3.5" />
                                                            Message
                                                        </button>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                ) : (
                                    <div className="text-center py-8">
                                        <Users className="w-10 h-10 text-[#D8B4E2] mx-auto mb-2" />
                                        <p className="text-[#4A4A68] text-sm">No connections yet</p>
                                        <p className="text-[#4A4A68] text-xs mt-1">Start connecting with other members!</p>
                                    </div>
                                )}
                            </div>

                            {/* Pending Sent */}
                            {pendingSent.length > 0 && (
                                <div className="bg-white border border-[#E5E0D8] rounded-sm p-6">
                                    <h3 className="font-serif text-lg text-[#1A1A2E] mb-4">Sent Requests</h3>
                                    <div className="space-y-2">
                                        {pendingSent.map(conn => (
                                            <div 
                                                key={conn.id}
                                                className="p-3 bg-[#F9F6F0] rounded-sm text-sm"
                                                data-testid={`sent-request-${conn.id}`}
                                            >
                                                <p className="text-[#1A1A2E] font-medium">To: {conn.to_user_name || 'Member'}</p>
                                                {conn.message && (
                                                    <p className="text-xs text-[#4A4A68] mt-1 italic">"{conn.message}"</p>
                                                )}
                                                <p className="text-xs text-[#5B2D8E] mt-1">Waiting for acceptance — conversation opens when accepted.</p>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </main>

            {/* Connection Request Dialog */}
            <Dialog open={connectDialogOpen} onOpenChange={setConnectDialogOpen}>
                <DialogContent className="w-[calc(100%-2rem)] sm:max-w-md bg-white max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="font-serif text-2xl text-[#1A1A2E]">
                            Connect with {selectedMember?.name}
                        </DialogTitle>
                    </DialogHeader>
                    <div className="mt-4 space-y-4">
                        <div className="p-4 bg-[#F9F6F0] rounded-sm">
                            <div className="flex items-center gap-3">
                                <div className="w-12 h-12 bg-[#5B2D8E] rounded-full flex items-center justify-center text-white font-serif text-xl">
                                    {selectedMember?.name?.charAt(0) || '?'}
                                </div>
                                <div>
                                    <p className="font-serif text-lg text-[#1A1A2E]">{selectedMember?.name}</p>
                                    <span className={getTierBadgeClass(selectedMember?.membership_tier)}>
                                        {selectedMember?.membership_tier}
                                    </span>
                                </div>
                            </div>
                        </div>

                        <div>
                            <label className="block font-sans text-sm text-[#1A1A2E] mb-2">
                                Message (optional)
                            </label>
                            <textarea
                                value={connectionMessage}
                                onChange={(e) => setConnectionMessage(e.target.value)}
                                placeholder="Introduce yourself..."
                                rows={3}
                                className="w-full resize-none"
                                data-testid="connection-message"
                            />
                        </div>

                        <button
                            onClick={handleSendRequest}
                            className="btn-primary w-full"
                            disabled={sending}
                            data-testid="send-request-btn"
                        >
                            {sending ? 'Sending...' : 'Send Connection Request'}
                        </button>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Chat Dialog */}
            <Dialog open={chatOpen} onOpenChange={setChatOpen}>
                <DialogContent className="sm:max-w-lg bg-white h-[80vh] flex flex-col">
                    <DialogHeader>
                        <DialogTitle className="font-serif text-xl text-[#1A1A2E] flex items-center gap-3">
                            <div className="w-10 h-10 bg-[#5B2D8E] rounded-full flex items-center justify-center text-white font-serif">
                                {activeConnection?.other_user_name?.charAt(0) || '?'}
                            </div>
                            {activeConnection?.other_user_name || 'Chat'}
                        </DialogTitle>
                    </DialogHeader>
                    
                    {/* Messages */}
                    <div className="flex-grow overflow-y-auto p-4 space-y-4 bg-[#F9F6F0] rounded-sm" data-testid="messages-container">
                        {loadingMessages ? (
                            <div className="text-center py-8">
                                <div className="w-6 h-6 border-2 border-[#5B2D8E] border-t-transparent rounded-full animate-spin mx-auto"></div>
                            </div>
                        ) : messages.length > 0 ? (
                            messages.map(msg => (
                                <div
                                    key={msg.id}
                                    className={`flex ${msg.sender_id === user?.id ? 'justify-end' : 'justify-start'}`}
                                >
                                    <div
                                        className={`max-w-[70%] p-3 rounded-sm ${
                                            msg.sender_id === user?.id
                                                ? 'bg-[#5B2D8E] text-white'
                                                : 'bg-white border border-[#E5E0D8]'
                                        }`}
                                    >
                                        {msg.is_request_message && (
                                            <p className={`text-[10px] uppercase tracking-widest mb-1 ${msg.sender_id === user?.id ? 'text-white/70' : 'text-[#5B2D8E]'}`} data-testid="request-message-label">
                                                Connection request
                                            </p>
                                        )}
                                        <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                                        <p className={`text-xs mt-1 ${msg.sender_id === user?.id ? 'text-white/60' : 'text-[#4A4A68]'}`}>
                                            {formatMessageTime(msg.created_at)}
                                        </p>
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div className="text-center py-8 text-[#4A4A68]">
                                <MessageSquare className="w-8 h-8 mx-auto mb-2 text-[#D8B4E2]" />
                                <p>No messages yet. Start the conversation!</p>
                            </div>
                        )}
                    </div>

                    {/* Message Input */}
                    <form onSubmit={handleSendMessage} className="flex gap-2 mt-4">
                        <input
                            type="text"
                            value={newMessage}
                            onChange={(e) => setNewMessage(e.target.value)}
                            placeholder="Type a message..."
                            className="flex-grow min-w-0 w-0"
                            data-testid="message-input"
                        />
                        <button
                            type="submit"
                            className="btn-primary px-4 shrink-0"
                            disabled={sendingMessage || !newMessage.trim()}
                            data-testid="send-message-btn"
                        >
                            <Send className="w-5 h-5" />
                        </button>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Profile Preview Dialog */}
            <Dialog open={profileOpen} onOpenChange={setProfileOpen}>
                <DialogContent className="w-[calc(100%-2rem)] sm:max-w-lg bg-white max-h-[90vh] overflow-y-auto" data-testid="profile-preview-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-serif text-2xl text-[#1A1A2E]">Member Profile</DialogTitle>
                    </DialogHeader>
                    {profileMember && (
                        <div className="mt-4 space-y-4" data-testid="member-profile-modal">
                            <div className="flex items-center gap-4">
                                {profileMember.profile_image_url ? (
                                    <img
                                        src={resolveImageUrl(profileMember.profile_image_url)}
                                        alt={profileMember.name}
                                        className="w-20 h-20 rounded-full object-cover border border-[#E5E0D8]"
                                    />
                                ) : (
                                    <div className="w-20 h-20 bg-[#5B2D8E] rounded-full flex items-center justify-center text-white font-serif text-3xl">
                                        {profileMember.name?.charAt(0) || '?'}
                                    </div>
                                )}
                                <div>
                                    <p className="font-serif text-2xl text-[#1A1A2E]">{profileMember.name}</p>
                                    <span className={getTierBadgeClass(profileMember.membership_tier)}>
                                        {profileMember.membership_tier}
                                    </span>
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-3 text-sm">
                                {(profileMember.city || profileMember.country) && (
                                    <div className="p-3 bg-[#F9F6F0] rounded-sm">
                                        <p className="text-xs uppercase tracking-wider text-[#4A4A68] mb-1">Location</p>
                                        <p className="text-[#1A1A2E] flex items-center gap-1">
                                            <MapPin className="w-4 h-4 text-[#5B2D8E]" />
                                            {[profileMember.city, profileMember.country].filter(Boolean).join(', ')}
                                        </p>
                                    </div>
                                )}
                                {profileMember.age_range && (
                                    <div className="p-3 bg-[#F9F6F0] rounded-sm">
                                        <p className="text-xs uppercase tracking-wider text-[#4A4A68] mb-1">Age Range</p>
                                        <p className="text-[#1A1A2E]">{profileMember.age_range}</p>
                                    </div>
                                )}
                            </div>

                            {profileMember.bio && (
                                <div className="p-4 bg-[#F2EBE5] rounded-sm">
                                    <p className="text-xs uppercase tracking-wider text-[#4A4A68] mb-2">About</p>
                                    <p className="font-sans text-[#1A1A2E] leading-relaxed whitespace-pre-wrap">{profileMember.bio}</p>
                                </div>
                            )}

                            {profileLoading && (
                                <p className="text-xs text-[#4A4A68] text-center">Loading full profile…</p>
                            )}

                            {/* Action buttons */}
                            {!connectedUserIds.includes(profileMember.id) && profileMember.id !== user?.id && connectionLimit !== 0 && canSendMore && (
                                <button
                                    onClick={() => {
                                        setSelectedMember(profileMember);
                                        setProfileOpen(false);
                                        setConnectDialogOpen(true);
                                    }}
                                    className="btn-primary w-full flex items-center justify-center gap-2"
                                    data-testid="profile-connect-btn"
                                >
                                    <UserPlus className="w-4 h-4" />
                                    Send Connection Request
                                </button>
                            )}
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            <Footer />
        </div>
    );
}
